import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} Hito
 * @property {number} meta - Threshold in the minor units Liquid uses (pesos * 100)
 * @property {string} etiqueta - Reward name
 * @property {string} icono - Icon name from snippets/emp-recompensas-icon.liquid
 *
 * @typedef {Object} EmpRecompensasRefs
 * @property {HTMLElement} [pendiente] - "Estás a $X de …" wrapper
 * @property {HTMLElement} [completo] - Message shown when every tier is unlocked
 * @property {HTMLElement} [chip] - The gold amount chip
 * @property {HTMLElement} [recompensa] - Name of the next reward
 * @property {HTMLElement} [progreso] - Wrapper that reserves room above and below the track
 * @property {HTMLElement} [relleno] - The bar fill
 * @property {HTMLElement} [hitos] - The <ol> of milestones
 * @property {HTMLTemplateElement} [iconos] - Hidden copy of every icon
 */

/** Kaching tier types mapped to the icons of this theme. */
const ICONO_POR_TIPO = [
  [/ship/i, 'camion'],
  [/gift|product/i, 'regalo'],
  [/discount|percent/i, 'porcentaje'],
];

/**
 * The PDP rewards bar (blocks/emp-recompensas.liquid).
 *
 * Liquid renders the fallback milestones written in the theme editor, which is
 * what shows without JS. On connect, this component asks Kaching Cart for its
 * own tiers and rebuilds the milestones when it has them, so a tier added in
 * the app shows up here with no theme change.
 *
 * Kaching replaces the cart drawer and does not fire Horizon's `cart:update`,
 * so the total is refreshed from two sides: the theme event, and a
 * PerformanceObserver that notices any request to /cart/add|change|update|clear.
 *
 * @extends {Component<EmpRecompensasRefs>}
 */
class EmpRecompensas extends Component {
  /** @type {Hito[]} */
  #hitos = [];
  /** @type {number} */
  #total = 0;
  /** @type {number | undefined} */
  #decimales;
  /** @type {PerformanceObserver | undefined} */
  #observador;
  /** @type {ResizeObserver | undefined} */
  #observadorDeTamano;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #temporizador;

  connectedCallback() {
    super.connectedCallback();

    this.#hitos = this.#leerHitosDelDom();
    this.#total = Number(this.dataset.total) || 0;

    if (this.dataset.fuente === 'kaching') {
      const tramos = this.#leerTramosDeKaching();
      if (tramos.length) {
        this.#hitos = tramos;
        this.#dibujarHitos();
      }
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#alActualizarCarrito);
    window.addEventListener('pageshow', this.#refrescarTotal);
    this.#observarPeticionesDelCarrito();
    this.#pintar();

    this.#observadorDeTamano = new ResizeObserver(() => this.#ajustarAlto());
    this.#observadorDeTamano.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#alActualizarCarrito);
    window.removeEventListener('pageshow', this.#refrescarTotal);
    this.#observador?.disconnect();
    this.#observadorDeTamano?.disconnect();
    clearTimeout(this.#temporizador);
  }

  /**
   * Reads the milestones Liquid already rendered.
   * @returns {Hito[]}
   */
  #leerHitosDelDom() {
    const elementos = this.refs.hitos?.querySelectorAll('.emp-recompensas__hito') ?? [];

    return [...elementos].map((elemento) => ({
      meta: Number(elemento.getAttribute('data-meta')) || 0,
      etiqueta: elemento.querySelector('.emp-recompensas__etiqueta')?.textContent?.trim() ?? '',
      icono: elemento.getAttribute('data-icono') || 'estrella',
    }));
  }

  /**
   * Reads the tiers of the Kaching Cart app embed, if it is on the page with an
   * active tiered promotions bar. Its thresholds come in whole currency units.
   * @returns {Hito[]}
   */
  #leerTramosDeKaching() {
    /** @type {any} */
    const props = /** @type {any} */ (window).__kachingCartProps;
    const barra = props?.cartSettings?.tieredPromotionsBar;
    if (!barra || barra.isActive === false) return [];

    const mercado = props?.market?.handle ?? props?.market?.id;
    const especificos = Array.isArray(barra.marketSpecificTiers)
      ? barra.marketSpecificTiers.find(
          (/** @type {any} */ entrada) => entrada?.market === mercado || entrada?.marketId === mercado
        )
      : null;
    const tramos = especificos?.tiers ?? barra.tiers;
    if (!Array.isArray(tramos) || !tramos.length) return [];

    return tramos
      .filter((/** @type {any} */ tramo) => Number(tramo?.threshold) > 0)
      .map((/** @type {any} */ tramo) => {
        const meta = Math.round(Number(tramo.threshold) * 100);
        const etiqueta = String(tramo.title ?? '').trim();

        return { meta, etiqueta, icono: this.#elegirIcono(meta, tramo.type) };
      })
      .sort((/** @type {Hito} */ a, /** @type {Hito} */ b) => a.meta - b.meta);
  }

  /**
   * An icon chosen in the theme editor for the same amount wins; otherwise the
   * tier type decides.
   * @param {number} meta - Threshold in minor units
   * @param {string} [tipo] - Kaching tier type, e.g. "shipping-discount"
   * @returns {string}
   */
  #elegirIcono(meta, tipo) {
    const manual = this.#hitos.find((hito) => hito.meta === meta);
    if (manual) return manual.icono;

    return ICONO_POR_TIPO.find(([patron]) => patron.test(tipo ?? ''))?.[1] ?? 'estrella';
  }

  /** Rebuilds the milestone list, reusing the icons Liquid left in the template. */
  #dibujarHitos() {
    const { hitos } = this.refs;
    if (!hitos) return;

    const molde = hitos.querySelector('.emp-recompensas__hito');
    if (!molde) return;

    const nuevos = this.#hitos.map((hito) => {
      const elemento = /** @type {HTMLElement} */ (molde.cloneNode(true));
      elemento.setAttribute('data-meta', String(hito.meta));
      elemento.setAttribute('data-icono', hito.icono);

      const etiqueta = elemento.querySelector('.emp-recompensas__etiqueta');
      if (etiqueta) etiqueta.textContent = hito.etiqueta;

      const circulo = elemento.querySelector('.emp-recompensas__circulo');
      const icono = this.refs.iconos?.content.querySelector(`[data-icono="${hito.icono}"] svg`);
      if (circulo && icono) circulo.replaceChildren(icono.cloneNode(true));

      return elemento;
    });

    hitos.replaceChildren(...nuevos);
    this.style.setProperty('--emp-rec-total', String(this.#hitos.length));
  }

  /**
   * @param {Event} evento - Horizon's cart:update event
   */
  #alActualizarCarrito = (evento) => {
    const detalle = /** @type {CustomEvent} */ (evento).detail;
    if (detalle?.data?.didError) return;

    const total = detalle?.resource?.total_price;
    if (typeof total === 'number') {
      this.#total = total;
      this.#pintar();
      return;
    }

    this.#refrescarTotal();
  };

  /** Reads /cart.js, for the updates that don't carry the total. */
  #refrescarTotal = async () => {
    /** @type {any} */
    const tema = /** @type {any} */ (window).Theme;
    const ruta = tema?.routes?.cart_url ?? '/cart';

    try {
      const respuesta = await fetch(`${ruta}.js`);
      if (!respuesta.ok) return;

      const carrito = await respuesta.json();
      if (typeof carrito?.total_price !== 'number') return;

      this.#total = carrito.total_price;
      this.#pintar();
    } catch {
      // Sin red no hay nada que actualizar: la barra se queda como está.
    }
  };

  /**
   * Kaching's drawer writes to the cart without firing the theme event, so the
   * cart requests themselves are the signal.
   */
  #observarPeticionesDelCarrito() {
    if (typeof PerformanceObserver === 'undefined') return;

    this.#observador = new PerformanceObserver((lista) => {
      const tocaCarrito = lista
        .getEntries()
        .some((entrada) => /\/cart\/(add|change|update|clear)/.test(entrada.name));
      if (!tocaCarrito) return;

      clearTimeout(this.#temporizador);
      this.#temporizador = setTimeout(this.#refrescarTotal, 350);
    });

    try {
      // Con `buffered` también llegan las peticiones anteriores a este módulo,
      // que se carga tarde: si el carrito cambió antes, la barra se pone al día.
      this.#observador.observe({ type: 'resource', buffered: true });
    } catch {
      this.#observador = undefined;
    }
  }

  /** Paints the head, the fill and the state of every milestone. */
  #pintar() {
    const { pendiente, completo, chip, recompensa, relleno, hitos } = this.refs;
    const total = this.#total;
    const cantidad = this.#hitos.length;
    if (!cantidad || !hitos) return;

    let porcentaje = 100;
    let anterior = 0;
    let siguiente = -1;

    this.#hitos.forEach((hito, indice) => {
      if (siguiente === -1 && total < hito.meta) {
        siguiente = indice;
        const tramo = hito.meta - anterior || 1;
        porcentaje = ((total - anterior) / tramo + indice) * (100 / cantidad);
      }
      anterior = hito.meta;
    });

    const elementos = hitos.querySelectorAll('.emp-recompensas__hito');
    elementos.forEach((elemento, indice) => {
      const hito = this.#hitos[indice];
      if (!hito) return;

      const estado = total >= hito.meta ? 'listo' : indice === siguiente ? 'siguiente' : 'pendiente';
      elemento.classList.toggle('emp-recompensas__hito--listo', estado === 'listo');
      elemento.classList.toggle('emp-recompensas__hito--siguiente', estado === 'siguiente');
      elemento.classList.toggle('emp-recompensas__hito--pendiente', estado === 'pendiente');
      /** @type {HTMLElement} */ (elemento).style.left = `${((indice + 1) * 100) / cantidad}%`;

      const monto = elemento.querySelector('.emp-recompensas__monto');
      if (monto) monto.textContent = this.#formatear(hito.meta);
    });

    if (relleno) relleno.style.width = `${Math.min(Math.max(porcentaje, 0), 100)}%`;

    const desbloqueado = siguiente === -1;
    if (pendiente) pendiente.hidden = desbloqueado;
    if (completo) completo.hidden = !desbloqueado;

    if (!desbloqueado) {
      const hito = this.#hitos[siguiente];
      if (chip && hito) chip.textContent = this.#formatear(hito.meta - total);
      if (recompensa && hito) recompensa.textContent = hito.etiqueta;
    }

    this.#ajustarAlto();
  }

  /**
   * The names hang below the track, out of the flow, so the CSS reserves room
   * for two lines: the safe guess without JS. Once the real height is known the
   * card can be as short as the names actually need.
   */
  #ajustarAlto() {
    const { progreso } = this.refs;
    if (!progreso) return;

    const etiquetas = [...progreso.querySelectorAll('.emp-recompensas__etiqueta')];
    const alto = Math.max(0, ...etiquetas.map((etiqueta) => /** @type {HTMLElement} */ (etiqueta).offsetHeight));

    if (!alto) {
      // Vista compacta: sin nombres, manda la hoja de estilos.
      progreso.style.removeProperty('--emp-rec-abajo');
      return;
    }

    progreso.style.setProperty('--emp-rec-abajo', `calc(var(--emp-rec-circulo, 26px) / 2 + ${alto + 5}px)`);
  }

  /**
   * Liquid keeps every amount multiplied by 100, including zero-decimal
   * currencies like CLP, while `formatMoney` expects the currency's own minor
   * units. Probing the format once tells us how many decimals to keep.
   * @param {number} centesimas - Amount as Liquid stores it
   * @returns {string}
   */
  #formatear(centesimas) {
    const formato = this.dataset.moneyFormat || '{{amount}}';
    const moneda = this.dataset.currency || 'CLP';

    if (this.#decimales === undefined) {
      this.#decimales = (formatMoney(1, '{{amount}}', moneda).split('.')[1] ?? '').length;
    }

    return formatMoney(Math.round((centesimas / 100) * 10 ** this.#decimales), formato, moneda);
  }
}

if (!customElements.get('emp-recompensas')) {
  customElements.define('emp-recompensas', EmpRecompensas);
}
